/* =====================================================================
 * DocxPage — 페이지 컨테이너 (여백, 편집 영역)
 * DOM 측정 기반 페이지 분배: 실제 브라우저 렌더링 높이를 측정하여 페이지 나눔
 * DOM을 편집의 source of truth로 사용.
 * ===================================================================== */

import React, { useCallback, useRef, useImperativeHandle, forwardRef, useMemo, useEffect, useState, useLayoutEffect } from 'react';
import styled from 'styled-components';
import type { DocxDocument, DocxBlock, DocxPageLayout } from '@auw/workspace-utils';
import { twipsToPx } from '@auw/workspace-utils';
import { DocxParagraphComponent } from './DocxParagraph';
import { DocxTableComponent } from './DocxTable';

/* ------------------------------------------------------------------ */
/* 페이지 레이아웃 계산 헬퍼                                               */
/* ------------------------------------------------------------------ */

interface PageMetrics {
  pageWidth: number;
  pageHeight: number;
  paddingTop: number;
  paddingBottom: number;
  paddingLeft: number;
  paddingRight: number;
  contentHeight: number;
  contentWidth: number;
}

function computePageMetrics(layout: DocxPageLayout): PageMetrics {
  const pageWidth = twipsToPx(layout.width);
  const pageHeight = twipsToPx(layout.height);
  const paddingTop = twipsToPx(layout.marginTop);
  const paddingBottom = twipsToPx(layout.marginBottom);
  const paddingLeft = twipsToPx(layout.marginLeft);
  const paddingRight = twipsToPx(layout.marginRight);
  const contentHeight = pageHeight - paddingTop - paddingBottom;
  const contentWidth = pageWidth - paddingLeft - paddingRight;
  return { pageWidth, pageHeight, paddingTop, paddingBottom, paddingLeft, paddingRight, contentHeight, contentWidth };
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const ScrollArea = styled.div`
  background: #ebebeb;
  padding: 20px 20px 0;
  border-radius: 0 0 16px 16px;
`;

const EditableWrapper = styled.div`
  /* focus 시 파란색 outline 제거 */
  &:focus {
    outline: none;
  }

  /* 글로벌 CSS 리셋 무효화 */
  & * {
    margin: revert;
    padding: revert;
    box-sizing: border-box;
  }

  & [contenteditable]:focus,
  & [data-block-type]:focus {
    outline: none;
  }
`;

interface PageSheetProps {
  $metrics: PageMetrics;
}

const PageSheet = styled.div<PageSheetProps>`
  background: white;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.08),
    0 4px 12px rgba(0, 0, 0, 0.06);
  margin: 0 auto 24px;
  padding: ${({ $metrics: m }) => `${m.paddingTop}px ${m.paddingRight}px ${m.paddingBottom}px ${m.paddingLeft}px`};
  width: ${({ $metrics: m }) => m.pageWidth}px;
  min-height: ${({ $metrics: m }) => m.pageHeight}px;
  box-sizing: border-box;
  font-family: 'Calibri', 'Arial', sans-serif;
  font-size: 11pt;
  line-height: 1.4;
  color: #000;
  cursor: text;
  position: relative;
  border-radius: 8px;
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 24px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 9pt;
  color: #999;
  pointer-events: none;
  user-select: none;
`;

/** 측정용 숨겨진 컨테이너 (페이지 스타일 동일, 화면 밖) */
const MeasureContainer = styled.div<PageSheetProps>`
  position: absolute;
  left: -9999px;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  /* PageSheet과 동일한 스타일 (렌더링 환경 일치) */
  padding: ${({ $metrics: m }) => `${m.paddingTop}px ${m.paddingRight}px ${m.paddingBottom}px ${m.paddingLeft}px`};
  width: ${({ $metrics: m }) => m.pageWidth}px;
  box-sizing: border-box;
  font-family: 'Calibri', 'Arial', sans-serif;
  font-size: 11pt;
  line-height: 1.4;
  color: #000;

  /* EditableWrapper와 동일한 리셋 */
  & * {
    margin: revert;
    padding: revert;
    box-sizing: border-box;
  }
`;

/* ------------------------------------------------------------------ */
/* 측정된 높이로 페이지 분배                                               */
/* ------------------------------------------------------------------ */

function splitByMeasuredHeights(
  blocks: DocxBlock[],
  heights: Map<string, number>,
  contentHeight: number,
): DocxBlock[][] {
  const pages: DocxBlock[][] = [[]];
  let currentHeight = 0;

  for (const block of blocks) {
    const blockId = block.type === 'paragraph' ? block.id : block.id;
    const height = heights.get(blockId) || 20; // 폴백

    if (currentHeight + height > contentHeight && pages[pages.length - 1].length > 0) {
      pages.push([]);
      currentHeight = 0;
    }

    pages[pages.length - 1].push(block);
    currentHeight += height;
  }

  if (pages.length === 0) pages.push([]);
  return pages;
}

/* ------------------------------------------------------------------ */
/* 블록 렌더 헬퍼                                                       */
/* ------------------------------------------------------------------ */

function renderBlock(block: DocxBlock, doc: DocxDocument) {
  if (block.type === 'paragraph') {
    return (
      <DocxParagraphComponent
        key={block.id}
        paragraph={block}
        document={doc}
      />
    );
  }
  if (block.type === 'table') {
    return (
      <DocxTableComponent
        key={block.id}
        table={block}
        document={doc}
      />
    );
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Ref handle                                                          */
/* ------------------------------------------------------------------ */

export interface DocxPageHandle {
  syncFromDom: () => Map<string, string>;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface DocxPageProps {
  document: DocxDocument;
  editable?: boolean;
  /** 번호매기기 단락 뒤에 새 단락 삽입 (새 단락 ID 반환) */
  onInsertNumberedParagraph?: (blockId: string, domTexts: Map<string, string>) => string | null;
  /** 번호매기기 제거 */
  onRemoveNumbering?: (blockId: string) => void;
}

export const DocxPage = forwardRef<DocxPageHandle, DocxPageProps>(
  ({ document: doc, editable = false, onInsertNumberedParagraph, onRemoveNumbering }, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    /** 다음 렌더 후 커서를 옮길 블록 ID */
    const focusTargetRef = useRef<string | null>(null);

    // 페이지 레이아웃 계산
    const metrics = useMemo(() => computePageMetrics(doc.pageLayout), [doc.pageLayout]);

    // ── 측정된 블록 높이 → 페이지 분배 ──
    const [measuredHeights, setMeasuredHeights] = useState<Map<string, number>>(new Map());
    const [measured, setMeasured] = useState(false);

    // 문서 body가 바뀌면 재측정 필요
    const bodyKey = useMemo(() => doc.body.map(b => b.id).join(','), [doc.body]);

    useEffect(() => {
      setMeasured(false);
    }, [bodyKey]);

    // 측정 패스: MeasureContainer에 블록 렌더 → DOM 높이 읽기
    useLayoutEffect(() => {
      if (measured || !measureRef.current) return;

      const container = measureRef.current;
      const blockEls = container.querySelectorAll('[data-block-id]');
      const heightMap = new Map<string, number>();

      blockEls.forEach((el) => {
        const id = el.getAttribute('data-block-id');
        if (id) {
          heightMap.set(id, el.getBoundingClientRect().height);
        }
      });

      if (heightMap.size > 0) {
        setMeasuredHeights(heightMap);
        setMeasured(true);
      }
    });

    // 페이지 분배 (측정 완료 후)
    const pages = useMemo(() => {
      if (!measured || measuredHeights.size === 0) {
        // 측정 전: 모든 블록을 1페이지에 (측정용)
        return [doc.body];
      }
      // 페이지 번호/하단 여백 오버레이로 인해 Word 대비 한두 줄 더 들어가는 현상 보정
      const PAGE_BOTTOM_RESERVE = 28;
      return splitByMeasuredHeights(
        doc.body,
        measuredHeights,
        Math.max(0, metrics.contentHeight - PAGE_BOTTOM_RESERVE),
      );
    }, [doc.body, measured, measuredHeights, metrics.contentHeight]);

    const syncFromDom = useCallback((): Map<string, string> => {
      const map = new Map<string, string>();
      if (!wrapperRef.current) return map;

      const blockEls = wrapperRef.current.querySelectorAll('[data-block-id]');
      blockEls.forEach((el) => {
        const id = el.getAttribute('data-block-id');
        if (id) {
          map.set(id, el.textContent || '');
        }
      });

      return map;
    }, []);

    useImperativeHandle(ref, () => ({ syncFromDom }), [syncFromDom]);

    /* ── 렌더 후 커서 이동 ── */
    useEffect(() => {
      if (!focusTargetRef.current || !wrapperRef.current) return;
      const targetId = focusTargetRef.current;
      focusTargetRef.current = null;

      requestAnimationFrame(() => {
        const targetEl = wrapperRef.current?.querySelector(
          `[data-block-id="${targetId}"]`,
        );
        if (!targetEl) return;

        const sel = window.getSelection();
        if (!sel) return;

        const range = window.document.createRange();
        const textNode = targetEl.querySelector('br') || targetEl;
        range.setStart(textNode, 0);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      });
    });

    /* ── Enter 키 가로채기 ── */
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key !== 'Enter' || e.shiftKey) return;

        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const anchor = sel.anchorNode;
        const blockEl = (
          anchor?.nodeType === 1
            ? (anchor as Element)
            : anchor?.parentElement
        )?.closest('[data-block-id]');
        if (!blockEl) return;

        const blockId = blockEl.getAttribute('data-block-id');
        if (!blockId) return;

        const para = doc.body.find(
          (b) => b.type === 'paragraph' && b.id === blockId,
        );
        if (
          !para ||
          para.type !== 'paragraph' ||
          !para.numbering ||
          para.numbering.numId === '0'
        ) return;

        e.preventDefault();

        const blockText = (blockEl.textContent || '').trim();
        const prefixEl = blockEl.querySelector('[contenteditable="false"]');
        const prefixText = prefixEl?.textContent || '';
        const contentText = blockText.replace(prefixText, '').trim();

        if (!contentText) {
          onRemoveNumbering?.(blockId);
          focusTargetRef.current = blockId;
          return;
        }

        const domTexts = syncFromDom();
        const newId = onInsertNumberedParagraph?.(blockId, domTexts);
        if (newId) {
          focusTargetRef.current = newId;
        }
      },
      [doc.body, onInsertNumberedParagraph, onRemoveNumbering, syncFromDom],
    );

    return (
      <ScrollArea>
        {/* 측정용 숨겨진 컨테이너 — 모든 블록을 한 번에 렌더링하여 높이 측정 */}
        {!measured && (
          <MeasureContainer ref={measureRef} $metrics={metrics}>
            {doc.body.map((block) => renderBlock(block, doc))}
          </MeasureContainer>
        )}

        {/* 실제 페이지 렌더링 */}
        <EditableWrapper
          ref={wrapperRef}
          contentEditable={editable}
          suppressContentEditableWarning
          onKeyDown={editable ? handleKeyDown : undefined}
        >
          {pages.map((pageBlocks, pageIdx) => (
            <PageSheet key={pageIdx} $metrics={metrics}>
              {pageBlocks.map((block) => renderBlock(block, doc))}
              <PageNumber contentEditable={false}>
                {pageIdx + 1}
              </PageNumber>
            </PageSheet>
          ))}
        </EditableWrapper>
      </ScrollArea>
    );
  },
);

DocxPage.displayName = 'DocxPage';
